import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Select, Checkbox, App, Row, Col, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { USER } from '../../../graphql/queries/users';
import { USER_UPDATE, USER_DELETE } from '../../../graphql/mutations/users';

const { Option } = Select;

interface UserFormData {
  name: string;
  email: string;
  roles: string[];
  isVerified: boolean;
}

const UserEdit: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);

  const { loading, data = {} } = useQuery(USER, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [updateUser] = useMutation(USER_UPDATE, {
    onCompleted: () => {
      message.success('User updated successfully!');
      navigate(`/admin/user/${id}`);
    },
    onError: error => {
      console.error(error);
      message.error('Failed to update user. Please check all fields.');
    },
  });

  const [deleteUser] = useMutation(USER_DELETE, {
    onCompleted: () => {
      message.success('User deleted successfully');
      navigate('/admin/users');
    },
    onError: error => {
      console.error(error);
      message.error('Failed to delete user');
    },
  });

  useEffect(() => {
    if (data?.user) {
      const { user } = data;
      setName(user.name || '');
      setEmail(user.email || '');
      setIsVerified(user.isVerified || false);
      setRoles(user.roles || []);

      form.setFieldsValue({
        name: user.name,
        email: user.email,
        roles: user.roles,
        isVerified: user.isVerified,
      });
    }
  }, [data, form]);

  const handleSave = async (values: UserFormData) => {
    await updateUser({
      variables: {
        id,
        ...values,
      },
    });
  };

  const showDeleteConfirm = () => {
    modal.confirm({
      title: 'Delete User',
      content: 'Are you sure you want to delete this user? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      async onOk() {
        await deleteUser({
          variables: { id },
        });
      },
    });
  };

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <Row justify="center">
      <Col xs={24} sm={20} md={16} lg={12}>
        <Card title="Edit User" hoverable style={{ margin: '20px 0' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              name,
              email,
              roles,
              isVerified,
            }}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please input user name!' }]}>
              <Input placeholder="Name" onChange={e => setName(e.target.value)} />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please input user email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}>
              <Input placeholder="Email" onChange={e => setEmail(e.target.value)} />
            </Form.Item>

            <Form.Item
              label="Roles"
              name="roles"
              rules={[{ required: true, message: 'Please select at least one role!' }]}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Select roles"
                onChange={value => setRoles(value)}>
                <Option value="USER">USER</Option>
                <Option value="ADMIN">ADMIN</Option>
                <Option value="CONTRACTOR">CONTRACTOR</Option>
              </Select>
            </Form.Item>

            <Form.Item name="isVerified" valuePropName="checked">
              <Checkbox onChange={e => setIsVerified(e.target.checked)}>Verified</Checkbox>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
              <Button danger style={{ marginLeft: '10px' }} onClick={showDeleteConfirm}>
                Delete
              </Button>
              <Button style={{ marginLeft: '10px' }} onClick={() => navigate('/admin/users')}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default UserEdit;
